import type {
  ScopeStatusResponse
} from '../services/api'

// Friendly names for the Google scope clusters surfaced in partial-grant
// messaging. Shared by the Settings integrations banner and the onboarding
// permissions step so both name the same thing the same way.
//
// `as const` because this table is also the source of the union below: adding
// a cluster is then one edit here, not one here and one in a hand-kept type.
const SCOPE_CLUSTER_LABELS = {
  mail_calendar: 'Mail & Calendar',
  meet: 'Google Meet',
  docs: 'Google Docs & Drive',
} as const

// The clusters the backend will accept, mirroring `ScopeCluster` in
// backend/src/app/services/oauth_scopes.py. Typing the callers that AUTHOR a
// cluster against this keeps a typo a compile error instead of a 400 whose
// `detail` ("Invalid scope cluster: x. Valid clusters: …") is engineer-written
// and now renders straight to the user (GET-12886).
//
// Deliberately NOT applied to `parseScopeClusters`/`missingScopeClusters`:
// those read a URL query param, so their input is genuinely unknown strings
// and narrowing them would be a lie. The union belongs where we write literals.
export type ScopeCluster = keyof typeof SCOPE_CLUSTER_LABELS

// Whether the user holds a cluster in full. The booleans below answer "is this
// usable", which is not the same question: `mail_calendar` is true off any
// Gmail plus any Calendar scope, so a cluster missing one permission inside it
// reads as fully connected. That is how a partial grant sailed through
// onboarding and showed 'Connected' in Settings while the assistant's
// proactive stack was switched off (GET-11570), so the server's per-cluster
// gap wins here — one definition for every surface that asks.
export function hasScopeCluster(
  status: ScopeStatusResponse,
  cluster: ScopeCluster,
): boolean {
  if ((status.incomplete_clusters?.[cluster] ?? []).length > 0) return false
  switch (cluster) {
    case 'mail_calendar': return status.mail_calendar
    case 'meet': return status.meet
    case 'docs': return status.docs
    default: return false
  }
}

// CallbackPage forwards the clusters we asked Google for as a comma-separated
// string in router state.
export function parseScopeClusters(requested: string | null | undefined): string[] {
  return (requested ?? '')
    .split(',')
    .map((cluster) => cluster.trim())
    .filter(Boolean)
}

// The narrowing guard for everything `parseScopeClusters` produces. A URL can
// carry any string, so this is where unknown input becomes a known cluster —
// and where an unrecognised one is dropped rather than forwarded to a backend
// that would answer with an engineer-written 400.
export function isScopeCluster(cluster: string): cluster is ScopeCluster {
  return Object.hasOwn(SCOPE_CLUSTER_LABELS, cluster)
}

// Which of the requested clusters came back ungranted — i.e. the boxes the user
// left unchecked on Google's consent screen. This is what lets a retry name the
// specific missing permission instead of restarting the generic step.
export function missingScopeClusters(
  status: ScopeStatusResponse,
  requested: string[],
): string[] {
  return requested
    .filter(isScopeCluster)
    .filter((cluster) => !hasScopeCluster(status, cluster))
}

export function formatScopeClusterList(clusters: string[]): string {
  const labels = clusters.map(
    (cluster) => (isScopeCluster(cluster) ? SCOPE_CLUSTER_LABELS[cluster] : cluster),
  )
  const last = labels.pop()
  if (last === undefined) return ''
  if (labels.length === 0) return last
  return `${labels.join(', ')} and ${last}`
}
